import { ref } from "vue";
import { flightAutocomplete } from "@/service/flight.api.js";

/**
 * Airport search composable for autocomplete functionality
 * @returns {Object} Airport search state and methods
 */
export function useAirportSearch() {
   const searchQuery = ref("");
   const results = ref([]);
   const isDropdownOpen = ref(false);
   const isLoading = ref(false);
   const selectedAirport = ref(null);
   const searchTimeout = ref(null);

   /**
    * Flattens airport results (handles both single airports and city groups)
    * @param {Array} airports - Airport data from API
    * @returns {Array} Flattened airport list
    */
   const flattenAirports = (airports) => {
      const result = [];
      airports.forEach((airport) => {
         if (airport.type === 1) {
            // Single airport
            result.push(airport);
         } else if (airport.type === 2 && airport.airports && airport.airports.length > 0) {
            // City group with multiple airports
            result.push(...airport.airports);
         }
      });
      return result;
   };

   /**
    * Handles search input and triggers API call with debounce
    */
   const handleSearch = async () => {
      if (searchTimeout.value) {
         clearTimeout(searchTimeout.value);
      }

      if (searchQuery.value.length < 2) {
         results.value = [];
         return;
      }

      searchTimeout.value = setTimeout(async () => {
         isLoading.value = true;
         try {
            const response = await flightAutocomplete({
               keyword: searchQuery.value,
            });

            if (response.result.success) {
               results.value = flattenAirports(response.airports || []);
            }
         } catch (error) {
            // Handle error silently
            results.value = [];
         } finally {
            isLoading.value = false;
         }
      }, 300);
   };

   /**
    * Selects an airport from results
    * @param {Object} airport - Selected airport object
    */
   const selectAirport = (airport) => {
      selectedAirport.value = airport;
      searchQuery.value = `${airport.name} (${airport.code})`;
      results.value = [];
      isDropdownOpen.value = false;
   };

   /**
    * Handles blur event with delay to allow click on results
    */
   const handleBlur = () => {
      setTimeout(() => {
         isDropdownOpen.value = false;
      }, 300);
   };

   /**
    * Handles focus event
    */
   const handleFocus = () => {
      isDropdownOpen.value = true;
   };

   /**
    * Resets the search state
    */
   const reset = () => {
      searchQuery.value = "";
      results.value = [];
      isDropdownOpen.value = false;
      isLoading.value = false;
      selectedAirport.value = null;
      if (searchTimeout.value) {
         clearTimeout(searchTimeout.value);
      }
   };

   return {
      // State
      searchQuery,
      results,
      isDropdownOpen,
      isLoading,
      selectedAirport,

      // Methods
      handleSearch,
      selectAirport,
      handleBlur,
      handleFocus,
      reset,
   };
}
